import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

/**
 * The notification surface: the bell's data, the read state, the per-channel
 * preferences and Web Push device registration.
 *
 * Every call here is bounded server-side to the signed-in user — the tables are
 * recipient-scoped by row-level security — so nothing in this file needs to
 * pass, or is able to influence, WHOSE notifications come back.
 */

export type NotificationSeverity = 'info' | 'warning' | 'critical';

export interface AppNotification {
  id: string;
  event_type: string;
  severity: NotificationSeverity;
  title: string;
  body: string;
  link: string;
  building_id: number | null;
  subject_type: string | null;
  subject_id: string | null;
  payload: Record<string, unknown>;
  /** The recipient muted this type in-app: kept in the list, ignored by the badge. */
  suppressed: boolean;
  read_at: string | null;
  created_at: string;
}

export interface NotificationPage {
  notifications: AppNotification[];
  unread: number;
  /** Cursor for the next page, or null when this page was the last one. */
  next_before: string | null;
}

export interface NotificationPreference {
  event_type: string;
  label: string;
  description: string;
  severity: NotificationSeverity;
  /** 'default' = the product's setting; 'override' = this user changed it. */
  source: 'default' | 'override';
  in_app: boolean;
  email: boolean;
  push: boolean;
}

export interface PreferencesResponse {
  channels: string[];
  /** False when the server has no VAPID keys — the push column is then inert. */
  push_available: boolean;
  preferences: NotificationPreference[];
}

export interface PushDevice {
  id: string;
  label: string;
  last_seen_at: string | null;
  active: boolean;
  created_at: string;
}

export const listNotifications = async (params?: {
  limit?: number;
  before?: string | null;
  unreadOnly?: boolean;
  buildingId?: number | null;
}): Promise<NotificationPage> => {
  const res = await axios.get(`${apiRoute}/notifications`, {
    params: {
      limit: params?.limit,
      before: params?.before || undefined,
      unread_only: params?.unreadOnly ? 'true' : undefined,
      building_id: params?.buildingId ?? undefined,
    },
  });
  // An answer without its list goes to the caller's catch. Production Firefox,
  // 2026-10-14: the list arrived as undefined, the bell stored it, and the
  // resident portal's live-refresh hook crashed on notifications.forEach (the
  // same unexplained class as fetchActivity and lookupResidents).
  if (!res.data || !Array.isArray((res.data as NotificationPage).notifications)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Lobbero] notifications: an answer without notifications',
      res.status,
      typeof res.data
    );
    throw new Error('notifications: an answer without notifications');
  }

  return res.data as NotificationPage;
};

export const fetchUnreadCount = async (): Promise<number> => {
  const res = await axios.get(`${apiRoute}/notifications/unread-count`);

  return Number(res.data?.unread || 0);
};

/** Mark specific rows read, or everything. Idempotent server-side. */
export const markRead = async (
  target: { ids: string[] } | { all: true }
): Promise<{ marked: number; unread: number }> => {
  const res = await axios.post(`${apiRoute}/notifications/read`, target);

  return res.data;
};

export const getPreferences = async (): Promise<PreferencesResponse> => {
  const res = await axios.get(`${apiRoute}/notifications/preferences`);

  return res.data;
};

export const setPreference = async (
  eventType: string,
  channels: { in_app: boolean; email: boolean; push: boolean }
): Promise<NotificationPreference> => {
  const res = await axios.put(`${apiRoute}/notifications/preferences`, {
    event_type: eventType,
    ...channels,
  });

  return res.data;
};

/** Drop the override so this type follows the product default again. */
export const resetPreference = async (eventType: string): Promise<void> => {
  await axios.delete(
    `${apiRoute}/notifications/preferences/${encodeURIComponent(eventType)}`
  );
};

/**
 * The VAPID public key, fetched rather than read from the build.
 *
 * This app bakes REACT_APP_* into the bundle at build time, so a key compiled
 * into a stale deploy would leave every device silently unable to receive
 * anything — no error, no failed request, just nothing. Asking the server means
 * the browser always subscribes against the key the server actually signs with.
 */
export const getPushKey = async (): Promise<{
  enabled: boolean;
  public_key: string | null;
}> => {
  const res = await axios.get(`${apiRoute}/notifications/push/key`);

  return res.data;
};

export const registerPushDevice = async (subscription: PushSubscription) => {
  const json = subscription.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };

  await axios.post(`${apiRoute}/notifications/push/subscribe`, {
    endpoint: json.endpoint,
    keys: json.keys,
  });
};

export const listPushDevices = async (): Promise<PushDevice[]> => {
  const res = await axios.get(`${apiRoute}/notifications/push/subscriptions`);

  return res.data?.subscriptions || [];
};

export const removePushDevice = async (id: string): Promise<void> => {
  await axios.delete(`${apiRoute}/notifications/push/subscriptions/${id}`);
};

/**
 * The WebSocket URL, derived from the same value axios uses so the two can
 * never point at different servers.
 *
 * `apiRoute` is absolute in local development (`http://localhost:8888/api`) and
 * RELATIVE in production (`/api`, same-origin behind Caddy). Both shapes have to
 * work, and only the absolute one carries a host — hence the `URL` constructor
 * with the page origin as its base, which resolves the relative case and leaves
 * the absolute one alone.
 */
export const notificationSocketUrl = (): string => {
  const base = new URL(apiRoute || '/api', window.location.origin);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = `${base.pathname.replace(/\/+$/, '')}/ws/notifications`;

  return base.toString();
};

/** Web Push needs the VAPID key as a Uint8Array; the server sends base64url. */
export const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }

  return output;
};
